import BottomSheet, { BottomSheetBackdrop, BottomSheetView } from '@gorhom/bottom-sheet';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import { MERGE_TAG_REGISTRY, type MergeTag } from '@/lib/mergeTags';

import { Heading } from './Heading';
import { Overline } from './Overline';
import { Text } from './Text';

const GROUPS = [
  { key: 'recipient', label: 'Recipient' },
  { key: 'your_stuff', label: 'Your stuff' },
] as const;

export interface InsertTagSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the chosen token. The compose screen inserts `{{token}}` at cursor. */
  onInsert: (token: MergeTag) => void;
}

/**
 * Bottom sheet for inserting merge tags into the Compose body. Two grouped
 * sections — Recipient + Your stuff — pulled from `MERGE_TAG_REGISTRY` so
 * adding a token in one place updates the sheet automatically.
 *
 * Tap inserts and dismisses; sheet doesn't preview the substitution itself
 * (that's Compose's preview block).
 */
export function InsertTagSheet({ isOpen, onClose, onInsert }: InsertTagSheetProps) {
  const sheetRef = useRef<BottomSheet>(null);
  const snapPoints = useMemo(() => ['60%'], []);

  useEffect(() => {
    if (!sheetRef.current) return;
    if (isOpen) sheetRef.current.snapToIndex(0);
    else sheetRef.current.close();
  }, [isOpen]);

  const handleSheetChange = useCallback(
    (index: number) => {
      if (index === -1) onClose();
    },
    [onClose],
  );

  const renderBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} appearsOnIndex={0} disappearsOnIndex={-1} />
    ),
    [],
  );

  const handleTap = useCallback(
    (token: MergeTag) => {
      onInsert(token);
      onClose();
    },
    [onClose, onInsert],
  );

  return (
    <BottomSheet
      ref={sheetRef}
      index={isOpen ? 0 : -1}
      snapPoints={snapPoints}
      enablePanDownToClose
      backdropComponent={renderBackdrop}
      onChange={handleSheetChange}
      handleIndicatorStyle={styles.handle}
      backgroundStyle={styles.background}
    >
      <BottomSheetView style={styles.content} accessibilityRole="menu">
        <Heading size="2xl">
          Insert tag
        </Heading>
        {GROUPS.map((group) => {
          const items = MERGE_TAG_REGISTRY.filter((t) => t.group === group.key);
          if (items.length === 0) return null;
          return (
            <View key={group.key} style={styles.section}>
              <Overline>{group.label}</Overline>
              <View style={styles.rows}>
                {items.map((item) => (
                  <TagRow
                    key={item.token}
                    label={item.label}
                    token={item.token}
                    sample={item.sample}
                    onPress={() => handleTap(item.token as MergeTag)}
                  />
                ))}
              </View>
            </View>
          );
        })}
      </BottomSheetView>
    </BottomSheet>
  );
}

interface TagRowProps {
  label: string;
  token: string;
  sample: string;
  onPress: () => void;
}

function TagRow({ label, token, sample, onPress }: TagRowProps) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Insert ${label} (${token}). Sample: ${sample}.`}
      style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
      testID={`insert-tag-${token}`}
    >
      <View style={styles.rowText}>
        <Text variant="bodySemiBold" color="deepGreen">
          {label}
        </Text>
        <Text style={styles.tokenMono} numberOfLines={1}>
          {`{{${token}}}`}
        </Text>
      </View>
      <Text variant="caption" color="textMuted" numberOfLines={1}>
        {sample}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  background: {
    backgroundColor: colors.background,
    borderTopLeftRadius: radii.xl,
    borderTopRightRadius: radii.xl,
  },
  handle: {
    backgroundColor: colors.border,
    width: 36,
  },
  content: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    paddingBottom: spacing['2xl'],
    gap: spacing.lg,
  },
  section: {
    gap: spacing.sm,
  },
  rows: {
    gap: spacing.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.base,
  },
  rowPressed: {
    backgroundColor: colors.border,
  },
  rowText: {
    flex: 1,
    gap: 2,
  },
  tokenMono: {
    fontFamily: fonts.body,
    fontSize: fontSizes.xs,
    color: colors.textMuted,
  },
});
